import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import c from 'classnames'
import ChevronDownIcon from 'mdi-react/ChevronDownIcon'

import NotFound404 from 'scenes/NotFound404'
import LoadingRing from 'presentation/LoadingRing'
import { CUSTOMER_SELF_MANAGEMENT } from 'routing/routes'
import { CustomerSelfManagementStoreContext } from 'providers/PublicStoreProvider'

import styles from './publicMainLayout.module.scss'

const FOOTER_VISIBLE_RATIO = 0.7

const PublicMainLayout = ({ header: Header, children, footer: Footer }) => {
    const history = useHistory()
    const { t } = useTranslation('customerSelfManagement')
    const footerRef = useRef(null)

    const customerSelfManagementStore = useContext(CustomerSelfManagementStoreContext)
    const { platform, logo, loadingPlatforms, platformResolved } = customerSelfManagementStore
    const isPlatformReady = !loadingPlatforms && platformResolved
    const [footerVisible, setFooterVisible] = useState(false)

    const goBack = () => {
        if (!platform) {
            window.location.replace(`${CUSTOMER_SELF_MANAGEMENT}/pasap`)
        } else {
            history.replace(`${CUSTOMER_SELF_MANAGEMENT}/${platform}`)
        }
    }

    const updateFooterVisibility = useCallback(() => {
        const footer = footerRef.current

        if (!footer) {
            return
        }

        const rect = footer.getBoundingClientRect()
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight

        if (rect.height <= 0) {
            setFooterVisible(false)
            return
        }

        const visibleTop = Math.max(rect.top, 0)
        const visibleBottom = Math.min(rect.bottom, viewportHeight)
        const visibleHeight = Math.max(0, visibleBottom - visibleTop)
        const ratio = visibleHeight / rect.height
        const fullyInView = rect.top >= -8 && rect.bottom <= viewportHeight + 8

        setFooterVisible(fullyInView || ratio >= FOOTER_VISIBLE_RATIO)
    }, [])

    useEffect(() => {
        if (!isPlatformReady || !platform) {
            setFooterVisible(false)
            return undefined
        }

        const footer = footerRef.current

        if (!footer) {
            return undefined
        }

        updateFooterVisibility()

        const intersectionObserver = new IntersectionObserver(
            () => updateFooterVisibility(),
            { threshold: [0, 0.25, 0.5, 0.7, 0.85, 1] },
        )

        intersectionObserver.observe(footer)
        window.addEventListener('scroll', updateFooterVisibility, { passive: true })
        window.addEventListener('resize', updateFooterVisibility)

        return () => {
            intersectionObserver.disconnect()
            window.removeEventListener('scroll', updateFooterVisibility)
            window.removeEventListener('resize', updateFooterVisibility)
        }
    }, [isPlatformReady, platform, updateFooterVisibility])

    const handleMoreInfoClick = () => {
        const footer = footerRef.current

        if (!footer) {
            return
        }

        if (footerVisible) {
            const footerTop = footer.getBoundingClientRect().top + window.scrollY
            const targetTop = Math.max(0, footerTop - window.innerHeight + 1)

            window.scrollTo({ top: targetTop, behavior: 'smooth' })
            return
        }

        footer.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    let content = <LoadingRing center />

    if (isPlatformReady) {
        content = platform ? children : <NotFound404 />
    }

    return (
        <div className={styles.publicWrapper}>
            <div className={styles.publicHeader}>
                {isPlatformReady && <Header platform={platform} goBack={goBack} logo={logo} />}
            </div>

            <main className={styles.publicMain}>
                {content}
            </main>

            <button
                type="button"
                className={c(styles.moreInfoTab, footerVisible && styles.moreInfoTabExpanded)}
                onClick={handleMoreInfoClick}
                aria-expanded={footerVisible}
            >
                <span>{footerVisible ? t('lessInfo') : t('moreInfo')}</span>
                <ChevronDownIcon size={16} className={styles.moreInfoChevron} />
            </button>

            <div id="public-footer" ref={footerRef} className={styles.publicFooter}>
                {isPlatformReady && <Footer platform={platform} />}
            </div>
        </div>
    )
}

PublicMainLayout.propTypes = {
    header: PropTypes.oneOfType([PropTypes.object]).isRequired,
    footer: PropTypes.oneOfType([PropTypes.object]).isRequired,
    children: PropTypes.node.isRequired,
}

export default observer(PublicMainLayout)
